export interface PopulationTier {
  label: string;
  lowerBound: number;
  upperBound: number | null;
  ratePerResident: number;
}

export interface PricingBreakdownItem extends PopulationTier {
  residents: number;
  subtotal: number;
}

export interface ContractDiscountTier {
  months: number;
  discountPercent: number;
}

export interface YearlyBreakdownItem {
  year: number;
  implementationFee: number;
  annualFee: number;
  total: number;
}

export interface MunicipalQuote {
  population: number;
  platformFee: number;
  residentFee: number;
  annualPrice: number;
  discountedAnnualPrice: number;
  implementationFee: number;
  contractMonths: number;
  contractYears: number;
  contractDiscountPercent: number;
  contractSubtotal: number;
  contractTotal: number;
  minimumApplied: boolean;
  breakdown: PricingBreakdownItem[];
  yearlyBreakdown: YearlyBreakdownItem[];
}

export const ANNUAL_PLATFORM_FEE = 3000;
export const MINIMUM_ANNUAL_PRICE = 3000;
export const IMPLEMENTATION_FEE = 1500;
export const MINIMUM_CONTRACT_MONTHS = 12;
export const MAXIMUM_CONTRACT_MONTHS = 60;
export const CONTRACT_MONTH_STEP = 12;

/** Longest matching tier wins (e.g. 48 months uses the 36-month / 10% rate). */
export const CONTRACT_DISCOUNT_TIERS: ContractDiscountTier[] = [
  { months: 12, discountPercent: 0 },
  { months: 24, discountPercent: 5 },
  { months: 36, discountPercent: 10 },
  { months: 60, discountPercent: 15 },
];

export const POPULATION_TIERS: PopulationTier[] = [
  {
    label: "First 10,000 residents",
    lowerBound: 0,
    upperBound: 10_000,
    ratePerResident: 0.25,
  },
  {
    label: "10,001–50,000 residents",
    lowerBound: 10_000,
    upperBound: 50_000,
    ratePerResident: 0.15,
  },
  {
    label: "50,001–200,000 residents",
    lowerBound: 50_000,
    upperBound: 200_000,
    ratePerResident: 0.08,
  },
  {
    label: "Residents above 200,000",
    lowerBound: 200_000,
    upperBound: null,
    ratePerResident: 0.04,
  },
];

const validateContractMonths = (contractMonths: number) => {
  if (
    !Number.isInteger(contractMonths) ||
    contractMonths < MINIMUM_CONTRACT_MONTHS ||
    contractMonths > MAXIMUM_CONTRACT_MONTHS ||
    contractMonths % CONTRACT_MONTH_STEP !== 0
  ) {
    throw new RangeError("Contract length must be 1–5 years in whole years.");
  }
};

export const getContractDiscountPercent = (contractMonths: number): number => {
  validateContractMonths(contractMonths);

  let discountPercent = 0;
  for (const tier of CONTRACT_DISCOUNT_TIERS) {
    if (contractMonths >= tier.months) {
      discountPercent = tier.discountPercent;
    }
  }
  return discountPercent;
};

export const calculateMunicipalQuote = (
  population: number,
  contractMonths = MINIMUM_CONTRACT_MONTHS
): MunicipalQuote => {
  if (!Number.isFinite(population) || population <= 0) {
    throw new RangeError("Population must be greater than zero.");
  }
  validateContractMonths(contractMonths);

  const residents = Math.round(population);
  const breakdown = POPULATION_TIERS.flatMap((tier) => {
    const tierEnd = tier.upperBound ?? residents;
    const tierResidents = Math.max(
      0,
      Math.min(residents, tierEnd) - tier.lowerBound
    );
    if (tierResidents === 0) return [];
    return [
      {
        ...tier,
        residents: tierResidents,
        subtotal: tierResidents * tier.ratePerResident,
      },
    ];
  });

  const residentFee = breakdown.reduce((total, item) => total + item.subtotal, 0);
  const uncappedAnnualPrice = ANNUAL_PLATFORM_FEE + residentFee;
  const minimumApplied = uncappedAnnualPrice < MINIMUM_ANNUAL_PRICE;
  const annualPrice = Math.round(
    Math.max(MINIMUM_ANNUAL_PRICE, uncappedAnnualPrice)
  );

  const contractYears = contractMonths / 12;
  const contractDiscountPercent = getContractDiscountPercent(contractMonths);
  const discountedAnnualPrice = Math.round(
    annualPrice * (1 - contractDiscountPercent / 100)
  );
  const yearlyBreakdown = Array.from({ length: contractYears }, (_, index) => {
    const implementationFee = index === 0 ? IMPLEMENTATION_FEE : 0;
    return {
      year: index + 1,
      implementationFee,
      annualFee: discountedAnnualPrice,
      total: implementationFee + discountedAnnualPrice,
    };
  });

  const contractSubtotal = IMPLEMENTATION_FEE + annualPrice * contractYears;
  const contractTotal = yearlyBreakdown.reduce(
    (total, item) => total + item.total,
    0
  );

  return {
    population: residents,
    platformFee: ANNUAL_PLATFORM_FEE,
    residentFee,
    annualPrice,
    discountedAnnualPrice,
    implementationFee: IMPLEMENTATION_FEE,
    contractMonths,
    contractYears,
    contractDiscountPercent,
    contractSubtotal,
    contractTotal,
    minimumApplied,
    breakdown,
    yearlyBreakdown,
  };
};

export const formatContractTerm = (months: number) => {
  const years = months / 12;
  const label = Number.isInteger(years) ? String(years) : years.toFixed(1);
  return `${label} ${years === 1 ? "year" : "years"}`;
};
